import { createFileRoute } from "@tanstack/react-router";
import {
  addDays,
  eachDayOfInterval,
  format,
  isSameDay,
  startOfDay,
} from "date-fns";
import { useLayoutEffect, useRef } from "react";
import { DayRow } from "@/components/meals/DayRow";
import { usePlannedMealsByDate } from "@/lib/meal-plan";

const DAYS_BEFORE_TODAY = 7;
const DAYS_AFTER_TODAY = 14;
const TODAY_INDEX = DAYS_BEFORE_TODAY;

// Finds the nearest scrollable ancestor (the <main> in _shell.tsx).
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}

// Sums offsetTop up the offsetParent chain from el to (not including)
// ancestor. Unlike getBoundingClientRect (what scrollIntoView uses under the
// hood), offsetTop reflects layout position only and is unaffected by CSS
// transforms — needed here because _shell.tsx's tab-switch crossfade applies
// a scale transform to the entering page while it's mounting, which would
// otherwise make scrollIntoView compute against a temporarily-compressed
// layout and leave the scroll position a few pixels short.
function offsetTopWithin(el: HTMLElement, ancestor: HTMLElement): number {
  let offset = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    offset += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return offset;
}

function scrollToToday(row: HTMLElement | null) {
  const container = getScrollParent(row);
  if (!row || !container) return;
  container.scrollTop = offsetTopWithin(row, container);
}

// Extra top margin at the three visual breakpoints: before today, after
// today, and after the coming-week block — everywhere else uses a small
// uniform gap. These gaps are what delineate the coming-week group now that
// it no longer has a distinct background. A plain space-y-* container can't
// express one-off larger gaps, so each row's spacing is computed explicitly.
function rowSpacing(index: number): string {
  if (index === 0) return "";
  if (
    index === TODAY_INDEX ||
    index === TODAY_INDEX + 1 ||
    index === TODAY_INDEX + 8
  ) {
    return "mt-6";
  }
  return "mt-2";
}

function MealsPage() {
  const today = useRef(startOfDay(new Date())).current;
  const plannedMealsByDate = usePlannedMealsByDate();

  const todayRowRef = useRef<HTMLAnchorElement>(null);

  // Scroll today's row into view on mount...
  useLayoutEffect(() => {
    scrollToToday(todayRowRef.current);
  }, []);

  // ...and re-correct once plannedMealsByDate resolves — its async arrival
  // can grow earlier rows (empty -> "has planned meals" is taller), shifting
  // today's position after the initial scroll.
  useLayoutEffect(() => {
    if (plannedMealsByDate === undefined) return;
    scrollToToday(todayRowRef.current);
  }, [plannedMealsByDate]);

  const days = eachDayOfInterval({
    start: addDays(today, -DAYS_BEFORE_TODAY),
    end: addDays(today, DAYS_AFTER_TODAY),
  });

  return (
    <div className="pb-24">
      {days.map((date, index) => (
        <div key={date.toISOString()} className={rowSpacing(index)}>
          <DayRow
            date={date}
            recipes={plannedMealsByDate?.get(format(date, "yyyy-MM-dd")) ?? []}
            isToday={isSameDay(date, today)}
            rowRef={isSameDay(date, today) ? todayRowRef : undefined}
          />
        </div>
      ))}
    </div>
  );
}

export const Route = createFileRoute("/_app/_shell/meals")({
  component: MealsPage,
});
